import * as React from 'react'

import { FieldOptions, FormLayout, SubmitButton, useForm } from '@saas-ui/forms'
import { Dialog } from '@saas-ui/react'

export interface InviteData {
  emails: string[]
  role?: 'admin' | 'member' | string
}

interface InviteInputs {
  emails: string
  role?: 'admin' | 'member' | string
}

export interface InviteDialogProps extends Omit<
  Dialog.RootProps,
  'onSubmit' | 'title' | 'scrollBehavior' | 'children'
> {
  title?: string
  onInvite(data: InviteData): Promise<unknown>
  roles?: FieldOptions
  requiredLabel?: string
  placeholder?: string
  onError?: (error: unknown) => void
  defaultValues?: InviteInputs
}

export const defaultMemberRoles = [
  {
    value: 'admin',
    label: 'Admin',
  },
  {
    value: 'member',
    label: 'Member',
  },
]

export function InviteDialog(props: InviteDialogProps) {
  const {
    onOpenChange,
    onInvite,
    onError,
    roles,
    defaultValues,
    title = 'Invite people',
    placeholder = 'example@company.com, example2@company.com',
    requiredLabel = 'Add at least one email address.',
    ...rest
  } = props

  const fieldRef = React.useRef(null)

  const form = useForm<InviteInputs>({
    defaultValues: {
      role: 'member',
      ...defaultValues,
    },
    onSubmit: async ({ emails, role }) => {
      try {
        await onInvite?.({
          emails: emails.split(',').map((email: string) => email.trim()),
          role,
        })

        onOpenChange?.({
          open: false,
        })
      } catch (e: unknown) {
        onError?.(e)
      }
    },
  })

  const roleOptions = roles || defaultMemberRoles

  return (
    <Dialog.Root
      {...rest}
      onOpenChange={onOpenChange}
      initialFocusEl={() => fieldRef.current}
    >
      <Dialog.Content portalled>
        <form.Form>
          <Dialog.Header>
            <Dialog.Title>{title}</Dialog.Title>
          </Dialog.Header>
          <Dialog.Body isolation="isolate">
            <FormLayout>
              <form.Field
                name="emails"
                type="textarea"
                placeholder={placeholder}
                rules={{ required: requiredLabel }}
                ref={fieldRef}
              />
              <form.Field
                label="Role"
                name="role"
                type="select"
                options={roleOptions}
                contentProps={{ portalled: false }}
              />
            </FormLayout>
          </Dialog.Body>
          <Dialog.Footer>
            <SubmitButton>Invite</SubmitButton>
          </Dialog.Footer>
        </form.Form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
